import { useQuery } from '@tanstack/react-query';
import { Outlet, useMatchRoute, useParams, useSearch } from '@tanstack/react-router';
import type { ProjectId, SquadId } from '@voll-intelligence/domain';

import { queryKeys } from '@web/core/query';
import { projectsRepository, squadsRepository } from '@web/data/repositories';
import { t } from '@web/i18n';
import {
  AppBar,
  AppIcons,
  BackButton,
  Breadcrumb,
  type BreadcrumbItem,
  TabBar,
  isAppIconName,
  useEscapeKey,
  useGoBack,
  useOrgContext,
} from '@web/ui/core';

type ActiveTab = 'identity' | 'settings';

function editorCopy(
  mode: 'create' | 'edit',
  squadName?: string,
): { title: string; eyebrow: string } {
  return mode === 'edit'
    ? {
        title: squadName ?? t('squad.editor.titleFallback'),
        eyebrow: t('squad.editor.eyebrowEdit'),
      }
    : { title: t('squad.editor.titleCreate'), eyebrow: t('squad.editor.eyebrowCreate') };
}

function editorReady(
  mode: 'create' | 'edit',
  isLoading: boolean,
  hasSquad: boolean,
  hasProjectId: boolean,
): boolean {
  return hasProjectId && (mode === 'create' || (!isLoading && hasSquad));
}

interface SquadEditorTabsProps {
  readonly orgSlug: string;
  readonly squadId: SquadId | undefined;
  readonly activeTab: ActiveTab;
  readonly settingsLocked: boolean;
}

function SquadEditorTabs({
  orgSlug,
  squadId,
  activeTab,
  settingsLocked,
}: SquadEditorTabsProps): React.JSX.Element {
  const hintLocked = t('squad.editor.tabs.hintLockedCreate');

  function identityTab(): React.JSX.Element {
    if (squadId) {
      return (
        <TabBar.Tab
          to="/orgs/$orgSlug/squad/$squadId/edit"
          params={{ orgSlug, squadId }}
          icon="identity"
          label={t('squad.editor.tabs.identity')}
          isActive={activeTab === 'identity'}
        />
      );
    }
    return (
      <TabBar.Tab
        icon="identity"
        label={t('squad.editor.tabs.identity')}
        isActive={activeTab === 'identity'}
        disabled
        hint={hintLocked}
      />
    );
  }

  function settingsTab(): React.JSX.Element {
    if (settingsLocked || !squadId) {
      return (
        <TabBar.Tab
          icon="settings"
          label={t('squad.editor.tabs.settings')}
          isActive={activeTab === 'settings'}
          disabled
          hint={hintLocked}
        />
      );
    }
    return (
      <TabBar.Tab
        to="/orgs/$orgSlug/squad/$squadId/edit/settings"
        params={{ orgSlug, squadId }}
        icon="settings"
        label={t('squad.editor.tabs.settings')}
        isActive={activeTab === 'settings'}
      />
    );
  }

  return (
    <TabBar ariaLabel={t('squad.editor.tabsAria')}>
      {identityTab()}
      {settingsTab()}
    </TabBar>
  );
}

/**
 * Squad editor shell. With the flattened `/squad/$squadId/...` and
 * `/squad/new` routes, the parent project id is no longer in the URL:
 * edit mode reads it from `GET /orgs/$orgId/squads/$squadId`, create
 * mode reads it from the `?projectId=` search param.
 */
export function SquadEditorLayout(): React.JSX.Element | null {
  const { squadId } = useParams({ strict: false });
  const search = useSearch({ strict: false });
  const matchRoute = useMatchRoute();
  const { orgId, orgSlug } = useOrgContext();
  const mode: 'create' | 'edit' = squadId ? 'edit' : 'create';

  const { data: squadDetail, isLoading: isSquadLoading } = useQuery({
    queryKey: queryKeys.squads.byId(orgId, squadId as SquadId),
    queryFn: () => squadsRepository.getSquadById(orgId, squadId as SquadId),
    enabled: !!squadId,
  });

  const projectId: ProjectId | undefined =
    mode === 'edit' ? squadDetail?.projectId : search.projectId;

  const { data: project } = useQuery({
    queryKey: queryKeys.projects.detail(orgId, projectId as ProjectId),
    queryFn: () => projectsRepository.getProject(orgId, projectId as ProjectId),
    enabled: !!projectId,
  });

  const goBack = useGoBack({
    to: projectId ? '/orgs/$orgSlug/project/$projectId/squads' : '/orgs/$orgSlug/overview',
    params: projectId ? { orgSlug, projectId } : { orgSlug },
  });

  useEscapeKey(goBack);

  const isReady = editorReady(mode, isSquadLoading, Boolean(squadDetail), Boolean(projectId));
  if (!isReady) return null;

  const { title: titleText, eyebrow } = editorCopy(mode, squadDetail?.name);
  const projectName = project?.name;
  const heroIcon = project?.icon && isAppIconName(project.icon) ? project.icon : 'users-four';

  const isSettings = !!matchRoute({
    to: '/orgs/$orgSlug/squad/$squadId/edit/settings',
  });
  const activeTab: ActiveTab = isSettings ? 'settings' : 'identity';
  const settingsLocked = mode === 'create';

  const crumbs: BreadcrumbItem[] = [
    { label: t('home.nav.process') },
    { label: t('home.crumb.projects') },
    ...(projectName ? [{ label: projectName }] : []),
    { label: t('home.crumb.squads') },
    { label: titleText },
  ];

  return (
    <div className="flex h-full flex-col overflow-y-auto bg-paper outline-none">
      <AppBar
        leading={
          <div className="inline-flex items-center gap-3.5">
            <BackButton label={t('squad.editor.actions.back')} onClick={goBack} />
            <Breadcrumb items={crumbs} />
          </div>
        }
      />

      <section className="border-b border-border-soft px-7 py-3.5">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-primary-tint text-primary-ink">
            <AppIcons name={heroIcon} size={16} />
          </span>
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="font-mono text-tiny font-medium uppercase tracking-eyebrow-wide text-faint-foreground">
              {projectName ? `${projectName} · ${eyebrow}` : eyebrow}
            </span>
            <h1 className="m-0 truncate font-serif text-h-display font-normal leading-display tracking-display">
              {titleText}
            </h1>
          </div>
        </div>
      </section>

      <SquadEditorTabs
        orgSlug={orgSlug}
        squadId={squadId as SquadId | undefined}
        activeTab={activeTab}
        settingsLocked={settingsLocked}
      />

      <Outlet />
    </div>
  );
}
